// The in-call chat's state: every message the Room has sent, and a way to
// write or delete one. Fed by the call store, which hands it every room
// message. The Room stores the chat for the call; this holds the same list so
// the panel renders at once, and `readAt` marks how far this person has read.

import { useCallback, useEffect, useRef, useState } from "react";
import { CHAT_LIMITS, type ChatMessage, type ClientMessage, type RoomMessage } from "@breeze/core";

const ERROR_MS = 4000;

function newId(): string {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID().replace(/-/g, "")
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export interface ChatStore {
  readonly messages: readonly ChatMessage[];
  /** Epoch ms of the newest message this person has seen. */
  readonly readAt: number;
  /** A refusal worth saying (too fast, too long), for a few seconds. */
  readonly problem: string | null;
  readonly send: (text: string) => void;
  readonly remove: (id: string) => void;
  /** Everything up to now has been seen. */
  readonly markRead: () => void;
  /** Handle a room message. True when it was a chat message. */
  readonly ingest: (message: RoomMessage) => boolean;
}

export function useChat(send: (message: ClientMessage) => void): ChatStore {
  const [messages, setMessages] = useState<readonly ChatMessage[]>([]);
  const [readAt, setReadAt] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const problemTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const sendRef = useRef(send);
  sendRef.current = send;

  useEffect(
    () => () => {
      if (problemTimer.current) clearTimeout(problemTimer.current);
    },
    [],
  );

  const say = useCallback((text: string) => {
    setProblem(text);
    if (problemTimer.current) clearTimeout(problemTimer.current);
    problemTimer.current = setTimeout(() => setProblem(null), ERROR_MS);
  }, []);

  const ingest = useCallback(
    (message: RoomMessage): boolean => {
      switch (message.type) {
        case "chatState":
          setMessages(message.messages);
          return true;
        case "chatMessage":
          setMessages((cur) => (cur.some((m) => m.id === message.message.id) ? cur : [...cur, message.message].slice(-CHAT_LIMITS.messages)));
          return true;
        case "chatRemoved":
          setMessages((cur) => cur.filter((m) => m.id !== message.id));
          return true;
        case "error":
          if (message.code === "chat") {
            say("That did not go through. Try again in a moment.");
            return true;
          }
          return false;
        default:
          return false;
      }
    },
    [say],
  );

  const sendText = useCallback((text: string) => {
    const clean = text.trim();
    if (!clean) return;
    sendRef.current({ type: "chatSend", id: newId(), text: clean });
  }, []);

  const remove = useCallback((id: string) => sendRef.current({ type: "chatDelete", id }), []);

  const markRead = useCallback(() => {
    const newest = messagesRef.current.at(-1)?.at ?? 0;
    setReadAt((cur) => (newest > cur ? newest : cur));
  }, []);

  return { messages, readAt, problem, send: sendText, remove, markRead, ingest };
}
